import React, { useCallback, useEffect, useState } from 'react';
import { AccountBalanceWallet, AddCircleOutline, Close } from '@mui/icons-material';
import comms from '../comms';

// Cash screen: shows the driver's running cash balance and recent deposits, and
// lets them record a cash hand-in ("Deposit Cash"). The deposit write goes
// through comms.driverCashier (POST /API/Driver/Cashier); in mock mode it is
// recorded locally so the whole flow is testable without a live ledger write.

function fmtMoney(v) {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? String(v) : `$${n.toFixed(2)}`;
}

function fmtDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function normalizeDeposits(res) {
  if (Array.isArray(res)) return res;
  const r = res?.result;
  if (Array.isArray(r)) return r;
  if (r && typeof r === 'object') return r.deposits || r.lineItems || [];
  return res?.deposits || res?.data || [];
}

function Cash({ userSettings }) {
  const driverId =
    userSettings?.Id || JSON.parse(localStorage.getItem('userSettings') || '{}')?.Id;

  const [balance, setBalance] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    if (!driverId) {
      setError('Driver not identified');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [bal, dep] = await Promise.all([
        comms.getDriverBalance(driverId).catch(() => null),
        comms.getDriverDeposits(driverId).catch(() => null),
      ]);
      const b = bal?.result?.balance ?? bal?.balance ?? bal?.result?.Balance;
      setBalance(b == null ? null : Number(b));
      setDeposits(normalizeDeposits(dep));
    } catch (e) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    load();
  }, [load]);

  const openForm = () => {
    setFormError(null);
    setAmount('');
    setNote('');
    setShowForm(true);
  };

  const handleSubmit = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      setFormError('Enter an amount greater than $0.00');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      const lineItems = [
        { type: 'Deposit', amount: amt, description: note.trim() || 'Cash drop' },
      ];
      await comms.driverCashier(driverId, lineItems);
      setShowForm(false);
      setToast(`Cash drop of ${fmtMoney(amt)} recorded`);
      await load();
      setTimeout(() => setToast(null), 3500);
    } catch (e) {
      setFormError(e?.message || 'Deposit failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-28 animate-pulse rounded-2xl bg-gray-200" />
        <div className="h-12 animate-pulse rounded-2xl bg-gray-200" />
        {[0, 1].map((i) => (
          <div key={i} className="h-16 animate-pulse rounded-2xl bg-gray-200" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Balance card */}
      <div className="rounded-2xl bg-gradient-to-br from-blue-800 to-blue-600 px-5 py-5 text-white shadow-sm">
        <div className="flex items-center gap-2 text-blue-100">
          <AccountBalanceWallet style={{ fontSize: 20 }} />
          <span className="text-sm font-medium">Cash on hand</span>
        </div>
        <div className="mt-2 text-3xl font-bold tracking-tight">
          {balance == null ? '—' : fmtMoney(balance)}
        </div>
        {error && <div className="mt-1 text-xs text-blue-100/90">{error}</div>}
      </div>

      {/* Deposit action */}
      {!showForm && (
        <button
          onClick={openForm}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-blue-700 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-transform active:scale-95"
        >
          <AddCircleOutline style={{ fontSize: 20 }} />
          Cash Drop
        </button>
      )}

      {/* Deposit form */}
      {showForm && (
        <div className="rounded-2xl bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-gray-900">Cash drop</h3>
            <button
              onClick={() => setShowForm(false)}
              aria-label="Cancel"
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100"
            >
              <Close style={{ fontSize: 20 }} />
            </button>
          </div>

          <label className="mb-1 block text-xs font-medium text-gray-500">Amount</label>
          <div className="flex items-center rounded-xl border border-gray-300 px-3 focus-within:border-blue-600">
            <span className="text-lg font-semibold text-gray-400">$</span>
            <input
              type="number"
              inputMode="decimal"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              autoFocus
              className="w-full bg-transparent px-2 py-3 text-lg font-semibold text-gray-900 outline-none"
            />
          </div>

          <label className="mb-1 mt-3 block text-xs font-medium text-gray-500">
            Note (optional)
          </label>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. End-of-shift cash drop"
            className="w-full rounded-xl border border-gray-300 px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-600"
          />

          {formError && (
            <div className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">
              {formError}
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <button
              onClick={() => setShowForm(false)}
              disabled={submitting}
              className="flex-1 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
            >
              {submitting ? 'Recording…' : 'Confirm drop'}
            </button>
          </div>
        </div>
      )}

      {/* Recent deposits */}
      <div>
        <h3 className="mb-2 px-1 text-sm font-semibold text-gray-700">Recent cash drops</h3>
        {deposits.length === 0 ? (
          <div className="rounded-2xl bg-white px-4 py-8 text-center text-sm text-gray-500 shadow-sm">
            No cash drops yet.
          </div>
        ) : (
          <div className="space-y-3">
            {deposits.map((row, i) => {
              const when = fmtDate(row.date || row.Date || row.createdOn || row.CreatedOn);
              const desc =
                row.description || row.Description || row.type || row.Type || row.note || 'Cash deposit';
              const amt = fmtMoney(row.amount ?? row.Amount ?? row.total ?? row.Total);
              return (
                <div
                  key={row.id || row.Id || i}
                  className="rounded-2xl bg-white px-4 py-3 shadow-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{when || `Item ${i + 1}`}</span>
                    {amt && <span className="text-sm font-semibold text-blue-700">{amt}</span>}
                  </div>
                  {desc && <div className="mt-1 text-sm text-gray-800">{desc}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Toast */}
      {toast && (
        <div className="fixed inset-x-0 bottom-6 z-[2100] flex justify-center px-4">
          <div className="rounded-full bg-gray-900 px-5 py-2.5 text-sm font-medium text-white shadow-lg">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}

export default Cash;
